import React, { Suspense, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from '@/context/LanguageContext';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { AuthProvider } from '@/context/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Only eagerly load the landing page (first paint) and auth callback
import LandingPage from '@/pages/LandingPage';
import AuthCallback from '@/pages/AuthCallback';

// Lazy load ALL other pages for code splitting
const QanunChatPage = React.lazy(() => import('@/pages/QanunChatPage'));
const PricingPage = React.lazy(() => import('@/pages/PricingPage'));
const DocumentAnalysisPage = React.lazy(() => import('@/pages/DocumentAnalysisPage'));
const LegalSearchPage = React.lazy(() => import('@/pages/LegalSearchPage'));
const ResearchHistoryPage = React.lazy(() => import('@/pages/ResearchHistoryPage'));
const TeamWorkspacePage = React.lazy(() => import('@/pages/TeamWorkspacePage'));
const ContractsPage = React.lazy(() => import('@/pages/ContractsPage'));
const CompareJurisdictionsPage = React.lazy(() => import('@/pages/CompareJurisdictionsPage'));
const SharedResearchPage = React.lazy(() => import('@/pages/SharedResearchPage'));
const AnalyticsDashboardPage = React.lazy(() => import('@/pages/AnalyticsDashboardPage'));
const MobileAppPage = React.lazy(() => import('@/pages/MobileAppPage'));
const AlertsPage = React.lazy(() => import('@/pages/AlertsPage'));
const FreeZonesPage = React.lazy(() => import('@/pages/FreeZonesPage'));
const ClauseLibraryPage = React.lazy(() => import('@/pages/ClauseLibraryPage'));
const RegulatoryTimelinePage = React.lazy(() => import('@/pages/RegulatoryTimelinePage'));
const CaseLawPage = React.lazy(() => import('@/pages/CaseLawPage'));
const ContractReviewPage = React.lazy(() => import('@/pages/ContractReviewPage'));
const ClientPortalPage = React.lazy(() => import('@/pages/ClientPortalPage'));
const LegalStrategyPage = React.lazy(() => import('@/pages/LegalStrategyPage'));
const ContractNegotiationPage = React.lazy(() => import('@/pages/ContractNegotiationPage'));
const EnterprisePage = React.lazy(() => import('@/pages/EnterprisePage'));
const MarketplacePage = React.lazy(() => import('@/pages/MarketplacePage'));
const NLPPage = React.lazy(() => import('@/pages/NLPPage'));
const WhitelabelPage = React.lazy(() => import('@/pages/WhitelabelPage'));
const CollabEditorPage = React.lazy(() => import('@/pages/CollabEditorPage'));
const CompareDocsPage = React.lazy(() => import('@/pages/CompareDocsPage'));
const CompliancePage = React.lazy(() => import('@/pages/CompliancePage'));
const WLMarketplacePage = React.lazy(() => import('@/pages/WLMarketplacePage'));
const AboutPage = React.lazy(() => import('@/pages/AboutPage'));
const FeaturesPage = React.lazy(() => import('@/pages/FeaturesPage'));
const TermsPage = React.lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('@/pages/PrivacyPage'));

// Loading fallback
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'inherit'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid #e5e7eb',
          borderTopColor: '#2563eb',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          margin: '0 auto 12px'
        }} />
        <p style={{ color: '#6b7280', fontSize: 14 }}>Loading...</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// Error boundary to prevent whole-app crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Page error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 20,
          fontFamily: 'Inter, system-ui, sans-serif'
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, color: '#111827' }}>
              Something went wrong
            </h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>
              This page encountered an error. Please try again.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = '/';
              }}
              style={{
                padding: '10px 24px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Go to Home
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <ErrorBoundary key={location.pathname}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/chat" element={<QanunChatPage />} />
          <Route path="/chat/:conversationId" element={<QanunChatPage />} />
          <Route path="/documents" element={<DocumentAnalysisPage />} />
          <Route path="/search" element={<LegalSearchPage />} />
          <Route path="/history" element={<ResearchHistoryPage />} />
          <Route path="/workspace" element={<TeamWorkspacePage />} />
          <Route path="/contracts" element={<ContractsPage />} />
          <Route path="/compare" element={<CompareJurisdictionsPage />} />
          <Route path="/shared/:shareId" element={<SharedResearchPage />} />
          <Route path="/analytics" element={<AnalyticsDashboardPage />} />
          <Route path="/mobile" element={<MobileAppPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/free-zones" element={<FreeZonesPage />} />
          <Route path="/clauses" element={<ClauseLibraryPage />} />
          <Route path="/timeline" element={<RegulatoryTimelinePage />} />
          <Route path="/cases" element={<CaseLawPage />} />
          <Route path="/contract-review" element={<ContractReviewPage />} />
          <Route path="/portal" element={<ClientPortalPage />} />
          <Route path="/portal/:portalId" element={<ClientPortalPage />} />
          <Route path="/strategy" element={<LegalStrategyPage />} />
          <Route path="/negotiate" element={<ContractNegotiationPage />} />
          <Route path="/enterprise" element={<EnterprisePage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/nlp" element={<NLPPage />} />
          <Route path="/whitelabel" element={<WhitelabelPage />} />
          <Route path="/collab/:collabId" element={<CollabEditorPage />} />
          <Route path="/compare-docs" element={<CompareDocsPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/wl-marketplace" element={<WLMarketplacePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

function AppContent() {
  const { theme } = useTheme();
  
  return (
    <div className={`App ${theme}`} data-theme={theme}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
